'use client'

import { motion, AnimatePresence } from 'framer-motion'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { Mail, Phone, MapPin, Clock, Send, MessageSquare, CheckCircle, AlertCircle, Award } from 'lucide-react'
import { useState } from 'react'
import Link from 'next/link'
import DynamicCompanyInfo from '@/components/DynamicCompanyInfo'

const contactSchema = z.object({
  firstName: z.string().min(2, 'Enter your first name'),
  lastName: z.string().min(1, 'Enter your last name'),
  email: z.string().email('Enter a valid email'),
  company: z.string().optional(),
  service: z.string().min(1, 'Please select a service'),
  message: z.string().min(10, 'Tell us a bit more (min 10 characters)'),
})

type ContactValues = z.infer<typeof contactSchema>

export default function Contact() {
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle')

  const {
    register, handleSubmit, reset,
    formState: { errors, isSubmitting },
  } = useForm<ContactValues>({
    resolver: zodResolver(contactSchema),
    mode: 'onBlur',
    defaultValues: { firstName: '', lastName: '', email: '', company: '', service: '', message: '' },
  })

  const onSubmit = async (data: ContactValues) => {
    try {
      const getInquiryType = (service: string) => {
        if (service.includes('recruitment') || service.includes('career')) return 'recruitment'
        if (service.includes('consulting') || service.includes('consultation')) return 'consultation'
        return 'project'
      }
      const response = await fetch('/api/general-inquiries', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: `${data.firstName} ${data.lastName}`.trim(),
          email: data.email, company: data.company,
          inquiryType: getInquiryType(data.service),
          service: data.service, message: data.message, source: 'contact'
        }),
      })
      if (response.ok) {
        setSubmitStatus('success')
        reset()
      } else { setSubmitStatus('error') }
    } catch { setSubmitStatus('error') }
  }

  const inputClasses = "w-full px-4 py-3 bg-white/5 border border-sky-200/20 rounded-xl text-white placeholder-slate-400 focus:ring-2 focus:ring-sky-400 focus:border-transparent transition-all duration-200 text-sm"
  const errCls = (field: keyof ContactValues) => (errors[field] ? ' border-red-400/60 ' : '')
  const FieldError = ({ field }: { field: keyof ContactValues }) => (
    <AnimatePresence>
      {errors[field] && (
        <motion.p
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: 'auto' }}
          exit={{ opacity: 0, height: 0 }}
          className="text-[11px] text-red-300 mt-1 flex items-center gap-1"
        >
          <AlertCircle className="w-3 h-3" /> {errors[field]?.message as string}
        </motion.p>
      )}
    </AnimatePresence>
  )

  return (
    <section id="contact" className="py-28 bg-gradient-to-b from-slate-900 via-slate-900 to-slate-950 relative overflow-hidden">
      <div className="absolute top-0 left-1/3 w-96 h-96 bg-sky-500/5 rounded-full blur-3xl" />
      <div className="absolute bottom-0 right-1/4 w-80 h-80 bg-blue-500/5 rounded-full blur-3xl" />

      <div className="container mx-auto px-6 relative z-10">
        {/* Header */}
        <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} viewport={{ once: true }} className="text-center mb-14">
          <motion.div initial={{ opacity: 0, scale: 0.8 }} whileInView={{ opacity: 1, scale: 1 }} viewport={{ once: true }}
            className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-sky-500/10 border border-sky-500/20 text-sky-300 text-sm font-medium mb-4">
            <MessageSquare className="w-4 h-4" /> Get In Touch
          </motion.div>
          <h2 className="text-4xl lg:text-5xl font-bold text-white mb-4">
            Ready to Start Your <span className="bg-gradient-to-r from-sky-400 to-blue-400 bg-clip-text text-transparent">Next Project?</span>
          </h2>
          <p className="text-lg text-slate-400 max-w-2xl mx-auto">
            Let's discuss how we can transform your business with innovative technology solutions.
          </p>
        </motion.div>

        <div className="grid lg:grid-cols-2 gap-12">
          {/* Form */}
          <motion.div initial={{ opacity: 0, x: -30 }} whileInView={{ opacity: 1, x: 0 }} transition={{ duration: 0.6 }} viewport={{ once: true }}
            className="bg-white/5 backdrop-blur-sm border border-sky-200/10 p-7 rounded-2xl">
            
            {submitStatus === 'success' && (
              <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }}
                className="mb-5 p-4 bg-emerald-500/10 border border-emerald-500/20 rounded-xl text-emerald-300 text-sm flex items-center gap-2">
                <CheckCircle className="w-4 h-4" /> Message sent successfully! We'll reply within 24 hours.
              </motion.div>
            )}
            {submitStatus === 'error' && (
              <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }}
                className="mb-5 p-4 bg-red-500/10 border border-red-500/20 rounded-xl text-red-300 text-sm">
                Something went wrong. Please try again.
              </motion.div>
            )}
            
            <form className="space-y-4" onSubmit={handleSubmit(onSubmit)} noValidate>
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="firstName" className="block text-xs font-medium text-slate-400 mb-1.5">First Name *</label>
                  <input type="text" id="firstName" {...register('firstName')} className={inputClasses + errCls('firstName')} placeholder="John" />
                  <FieldError field="firstName" />
                </div>
                <div>
                  <label htmlFor="lastName" className="block text-xs font-medium text-slate-400 mb-1.5">Last Name *</label>
                  <input type="text" id="lastName" {...register('lastName')} className={inputClasses + errCls('lastName')} placeholder="Doe" />
                  <FieldError field="lastName" />
                </div>
              </div>
              <div>
                <label htmlFor="email" className="block text-xs font-medium text-slate-400 mb-1.5">Email *</label>
                <input type="email" id="email" {...register('email')} className={inputClasses + errCls('email')} placeholder="john@example.com" />
                <FieldError field="email" />
              </div>
              <div>
                <label htmlFor="company" className="block text-xs font-medium text-slate-400 mb-1.5">Company</label>
                <input type="text" id="company" {...register('company')} className={inputClasses} placeholder="Your Company" />
              </div>
              <div>
                <label htmlFor="service" className="block text-xs font-medium text-slate-400 mb-1.5">Service *</label>
                <select id="service" {...register('service')} className={inputClasses + errCls('service')}>
                  <option value="" className="text-gray-800">Select a service</option>
                  <option value="website-development" className="text-gray-800">Website Development</option>
                  <option value="mobile-app" className="text-gray-800">Mobile App Development</option>
                  <option value="software-development" className="text-gray-800">Custom Software</option>
                  <option value="cloud-solutions" className="text-gray-800">Cloud Solutions</option>
                  <option value="cybersecurity" className="text-gray-800">Cybersecurity</option>
                  <option value="data-analytics" className="text-gray-800">Data Analytics</option>
                  <option value="recruitment-services" className="text-gray-800">Recruitment Services</option>
                  <option value="it-consulting" className="text-gray-800">IT Consulting</option>
                  <option value="industry-solutions" className="text-gray-800">Industry Solutions</option>
                </select>
                <FieldError field="service" />
              </div>
              <div>
                <label htmlFor="message" className="block text-xs font-medium text-slate-400 mb-1.5">Message *</label>
                <textarea id="message" rows={4} {...register('message')} className={`${inputClasses}${errCls('message')} resize-none`} placeholder="Tell us about your project..." />
                <FieldError field="message" />
              </div>
              <motion.button whileHover={{ scale: 1.01 }} whileTap={{ scale: 0.98 }} type="submit" disabled={isSubmitting}
                className="w-full bg-gradient-to-r from-sky-500 to-blue-600 text-white py-3 px-6 rounded-xl font-semibold text-sm hover:shadow-lg hover:shadow-sky-500/25 transition-all duration-300 flex items-center justify-center disabled:opacity-50">
                <Send className="w-4 h-4 mr-2" />
                {isSubmitting ? 'Sending...' : 'Send Message'}
              </motion.button>
            </form>
          </motion.div>

          {/* Contact Info Side */}
          <motion.div initial={{ opacity: 0, x: 30 }} whileInView={{ opacity: 1, x: 0 }} transition={{ duration: 0.6 }} viewport={{ once: true }} className="space-y-5">
            <div className="space-y-4">
              <DynamicCompanyInfo type="email">
                {(companyInfo: any) => {
                  if (!companyInfo?.contact?.email?.trim()) return null
                  return (
                    <div className="group bg-white/5 border border-sky-200/10 p-5 rounded-2xl hover:bg-white/10 transition-all duration-300">
                      <div className="flex items-center gap-4">
                        <div className="w-11 h-11 bg-gradient-to-r from-sky-500 to-cyan-500 rounded-xl flex items-center justify-center text-white group-hover:scale-110 transition-transform">
                          <Mail className="w-5 h-5" />
                        </div>
                        <div>
                          <h4 className="font-semibold text-white text-sm">Email Us</h4>
                          <p className="text-slate-400 text-sm">{companyInfo.contact.email}</p>
                        </div>
                      </div>
                    </div>
                  )
                }}
              </DynamicCompanyInfo>

              <DynamicCompanyInfo type="phone">
                {(companyInfo: any) => {
                  if (!companyInfo?.contact?.phone?.trim()) return null
                  return (
                    <div className="group bg-white/5 border border-sky-200/10 p-5 rounded-2xl hover:bg-white/10 transition-all duration-300">
                      <div className="flex items-center gap-4">
                        <div className="w-11 h-11 bg-gradient-to-r from-emerald-500 to-green-500 rounded-xl flex items-center justify-center text-white group-hover:scale-110 transition-transform">
                          <Phone className="w-5 h-5" />
                        </div>
                        <div>
                          <h4 className="font-semibold text-white text-sm">Call Us</h4>
                          <p className="text-slate-400 text-sm">{companyInfo.contact.phone}</p>
                        </div>
                      </div>
                    </div>
                  )
                }}
              </DynamicCompanyInfo>

              <DynamicCompanyInfo type="address">
                {(companyInfo: any) => {
                  const addr = companyInfo?.address
                  if (!addr?.fullAddress?.trim() && !addr?.street?.trim() && !addr?.city?.trim()) return null
                  return (
                    <div className="group bg-white/5 border border-sky-200/10 p-5 rounded-2xl hover:bg-white/10 transition-all duration-300">
                      <div className="flex items-center gap-4">
                        <div className="w-11 h-11 bg-gradient-to-r from-violet-500 to-purple-500 rounded-xl flex items-center justify-center text-white group-hover:scale-110 transition-transform">
                          <MapPin className="w-5 h-5" />
                        </div>
                        <div>
                          <h4 className="font-semibold text-white text-sm">Visit Us</h4>
                          <p className="text-slate-400 text-sm">{addr?.fullAddress || [addr?.street, addr?.city, addr?.state].filter(Boolean).join(', ')}</p>
                        </div>
                      </div>
                    </div>
                  )
                }}
              </DynamicCompanyInfo>

              <DynamicCompanyInfo type="companyName">
                {(companyInfo: any) => {
                  if (!companyInfo?.businessInfo?.businessHours?.trim()) return null
                  return (
                    <div className="group bg-white/5 border border-sky-200/10 p-5 rounded-2xl hover:bg-white/10 transition-all duration-300">
                      <div className="flex items-center gap-4">
                        <div className="w-11 h-11 bg-gradient-to-r from-orange-500 to-red-500 rounded-xl flex items-center justify-center text-white group-hover:scale-110 transition-transform">
                          <Clock className="w-5 h-5" />
                        </div>
                        <div>
                          <h4 className="font-semibold text-white text-sm">Business Hours</h4>
                          <p className="text-slate-400 text-sm whitespace-pre-line">{companyInfo.businessInfo.businessHours}</p>
                        </div>
                      </div>
                    </div>
                  )
                }}
              </DynamicCompanyInfo>
            </div>

            {/* Why Choose Us */}
            <div className="bg-gradient-to-br from-sky-500/10 to-blue-500/10 border border-sky-500/20 rounded-2xl p-5">
              <h4 className="font-semibold text-white mb-3 flex items-center gap-2 text-sm">
                <Award className="w-4 h-4 text-amber-400" /> Why Choose Us?
              </h4>
              <div className="space-y-2">
                {['Free consultation & analysis', '24/7 support & maintenance', 'On-time delivery focus', 'Transparent pricing', 'Senior-led engineering'].map((point, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <CheckCircle className="w-3.5 h-3.5 text-emerald-400 flex-shrink-0" />
                    <span className="text-slate-400 text-xs">{point}</span>
                  </div>
                ))}
              </div>
              <div className="mt-4 pt-3 border-t border-sky-500/20 flex items-center justify-between">
                <div className="flex gap-4">
                  <div className="text-center"><div className="text-sm font-bold text-white">End-to-End</div><div className="text-[10px] text-slate-500">Delivery</div></div>
                  <div className="text-center"><div className="text-sm font-bold text-white">Agile</div><div className="text-[10px] text-slate-500">Process</div></div>
                  <div className="text-center"><div className="text-sm font-bold text-white">24/7</div><div className="text-[10px] text-slate-500">Support</div></div>
                </div>
                <Link href="/about" className="text-sky-400 hover:text-sky-300 text-xs font-medium">Learn More →</Link>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  )
}
